import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline as CompletarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  RadioButtonUnchecked as PendienteIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import crmActividadService from '../../services/crmActividadService';
import type { CrmActividad } from '../../types/crm';
import { ROUTES } from '../../config/routes';
import { getApiErrorMessage } from '../../utils/apiError';

type VistaAgenda = 'dia' | 'semana' | 'mes';

const toDateKey = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseLocalDate = (value: string): Date => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const startOfWeek = (d: Date): Date => {
  const day = d.getDay();
  const diff = (day + 6) % 7;
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  r.setDate(r.getDate() - diff);
  return r;
};

const addDays = (d: Date, n: number): Date => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);

const endOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const formatRangeLabel = (vista: VistaAgenda, ancla: Date): string => {
  if (vista === 'dia') {
    return ancla.toLocaleDateString('es-AR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
  if (vista === 'semana') {
    const ini = startOfWeek(ancla);
    const fin = addDays(ini, 6);
    return `${ini.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })} – ${fin.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  return ancla.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
};

const formatTime = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
  return hasTime ? d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : '';
};

const CrmAgenda: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [vista, setVista] = useState<VistaAgenda>('semana');
  const [ancla, setAncla] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [actividades, setActividades] = useState<CrmActividad[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<CrmActividad | null>(null);
  const [savingEstado, setSavingEstado] = useState(false);

  const rango = useMemo(() => {
    if (vista === 'dia') {
      return { desde: ancla, hasta: ancla };
    }
    if (vista === 'semana') {
      const ini = startOfWeek(ancla);
      return { desde: ini, hasta: addDays(ini, 6) };
    }
    return { desde: startOfMonth(ancla), hasta: endOfMonth(ancla) };
  }, [vista, ancla]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await crmActividadService.getAgenda({
        fechaDesde: toDateKey(rango.desde),
        fechaHasta: toDateKey(rango.hasta),
        soloPendientes: soloPendientes || undefined,
      });
      setActividades(data ?? []);
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo cargar la agenda'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [rango, soloPendientes, enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const byDay = useMemo(() => {
    const map = new Map<string, CrmActividad[]>();
    actividades.forEach((a) => {
      if (!a.fechaVencimiento) return;
      const key = toDateKey(parseLocalDate(a.fechaVencimiento));
      const list = map.get(key) ?? [];
      list.push(a);
      map.set(key, list);
    });
    return map;
  }, [actividades]);

  const daysToShow = useMemo(() => {
    const days: Date[] = [];
    let cur = new Date(rango.desde);
    const end = rango.hasta;
    while (cur <= end) {
      days.push(new Date(cur));
      cur = addDays(cur, 1);
    }
    return days;
  }, [rango]);

  const navigatePeriod = (dir: -1 | 1) => {
    setAncla((prev) => {
      if (vista === 'dia') return addDays(prev, dir);
      if (vista === 'semana') return addDays(prev, dir * 7);
      return new Date(prev.getFullYear(), prev.getMonth() + dir, 1);
    });
  };

  const handleToggleEstado = async (actividad: CrmActividad) => {
    try {
      setSavingEstado(true);
      const updated = await crmActividadService.setEstado(actividad.id, !actividad.completada);
      setActividades((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
      setSelected((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
      enqueueSnackbar(
        updated.completada ? 'Actividad marcada como completada' : 'Actividad marcada como pendiente',
        { variant: 'success' },
      );
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo cambiar el estado'), { variant: 'error' });
    } finally {
      setSavingEstado(false);
    }
  };

  const renderActividadCard = (a: CrmActividad) => (
    <Box
      key={a.id}
      onClick={() => setSelected(a)}
      sx={{
        p: 1.25,
        border: '1px solid',
        borderColor: a.completada ? 'success.light' : 'divider',
        borderRadius: 2,
        bgcolor: a.completada ? 'rgba(46, 125, 50, 0.04)' : '#fff',
        cursor: 'pointer',
        '&:hover': { borderColor: 'primary.main', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip size="small" label={a.tipo} color="primary" variant="outlined" />
        <Chip
          size="small"
          label={a.completada ? 'Completada' : 'Pendiente'}
          color={a.completada ? 'success' : 'warning'}
        />
        {formatTime(a.fechaVencimiento) && (
          <Typography variant="caption" color="text.secondary">
            {formatTime(a.fechaVencimiento)}
          </Typography>
        )}
      </Stack>
      <Typography variant="subtitle2" sx={{ mt: 0.5, fontWeight: 600 }}>
        {a.titulo}
      </Typography>
      {(a.contactoNombre || a.oportunidadNombre) && (
        <Typography variant="caption" color="text.secondary" display="block">
          {[a.contactoNombre, a.oportunidadNombre].filter(Boolean).join(' · ')}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1.5}
            >
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Agenda
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={soloPendientes}
                    onChange={(e) => setSoloPendientes(e.target.checked)}
                    size="small"
                  />
                }
                label="Solo pendientes"
              />
            </Stack>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
              spacing={1.5}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={vista}
                onChange={(_, v: VistaAgenda | null) => {
                  if (v) setVista(v);
                }}
              >
                <ToggleButton value="dia" sx={{ textTransform: 'none', px: 2 }}>
                  Día
                </ToggleButton>
                <ToggleButton value="semana" sx={{ textTransform: 'none', px: 2 }}>
                  Semana
                </ToggleButton>
                <ToggleButton value="mes" sx={{ textTransform: 'none', px: 2 }}>
                  Mes
                </ToggleButton>
              </ToggleButtonGroup>

              <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="center">
                <IconButton onClick={() => navigatePeriod(-1)} size="small" aria-label="Anterior">
                  <ChevronLeftIcon />
                </IconButton>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, minWidth: { xs: 160, sm: 260 }, textAlign: 'center', textTransform: 'capitalize' }}
                >
                  {formatRangeLabel(vista, ancla)}
                </Typography>
                <IconButton onClick={() => navigatePeriod(1)} size="small" aria-label="Siguiente">
                  <ChevronRightIcon />
                </IconButton>
                <Button
                  size="small"
                  startIcon={<TodayIcon />}
                  onClick={() => {
                    const d = new Date();
                    d.setHours(0, 0, 0, 0);
                    setAncla(d);
                  }}
                  sx={{ textTransform: 'none', ml: 1 }}
                >
                  Hoy
                </Button>
              </Stack>
            </Stack>

            {loading && (
              <Typography variant="body2" color="text.secondary">
                Cargando agenda...
              </Typography>
            )}

            {!loading && actividades.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No hay actividades con vencimiento en este período.
              </Typography>
            )}

            {!loading && vista === 'dia' && (
              <Stack spacing={1.25}>
                {(byDay.get(toDateKey(ancla)) ?? []).map(renderActividadCard)}
              </Stack>
            )}

            {!loading && vista === 'semana' && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(7, 1fr)',
                  },
                  gap: 1.25,
                }}
              >
                {daysToShow.map((day) => {
                  const key = toDateKey(day);
                  const items = byDay.get(key) ?? [];
                  const isToday = key === toDateKey(new Date());
                  return (
                    <Box
                      key={key}
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: isToday ? 'primary.main' : 'divider',
                        bgcolor: isToday ? 'rgba(25, 118, 210, 0.04)' : '#fafafa',
                        minHeight: 120,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, display: 'block', mb: 1, textTransform: 'capitalize' }}
                      >
                        {day.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric' })}
                      </Typography>
                      <Stack spacing={1}>
                        {items.map(renderActividadCard)}
                        {items.length === 0 && (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            )}

            {!loading && vista === 'mes' && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: 0.75,
                }}
              >
                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((label) => (
                  <Typography
                    key={label}
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, textAlign: 'center', py: 0.5 }}
                  >
                    {label}
                  </Typography>
                ))}
                {Array.from({ length: (startOfMonth(ancla).getDay() + 6) % 7 }).map((_, i) => (
                  <Box key={`pad-${i}`} />
                ))}
                {daysToShow.map((day) => {
                  const key = toDateKey(day);
                  const items = byDay.get(key) ?? [];
                  const isToday = key === toDateKey(new Date());
                  return (
                    <Box
                      key={key}
                      onClick={() => {
                        setAncla(day);
                        setVista('dia');
                      }}
                      sx={{
                        p: 0.75,
                        minHeight: 72,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: isToday ? 'primary.main' : 'divider',
                        bgcolor: items.length ? '#fff' : '#fafafa',
                        cursor: 'pointer',
                        '&:hover': { borderColor: 'primary.light' },
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {day.getDate()}
                      </Typography>
                      {items.length > 0 && (
                        <Typography variant="caption" color="primary" display="block">
                          {items.length} act.
                        </Typography>
                      )}
                      {items.some((a) => !a.completada) && (
                        <Chip
                          size="small"
                          label={`${items.filter((a) => !a.completada).length} pend.`}
                          color="warning"
                          sx={{ mt: 0.25, height: 18, fontSize: 10 }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        {selected && (
          <>
            <DialogTitle sx={{ fontWeight: 600 }}>{selected.titulo}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={selected.tipo} color="primary" variant="outlined" />
                  <Chip
                    size="small"
                    label={selected.completada ? 'Completada' : 'Pendiente'}
                    color={selected.completada ? 'success' : 'warning'}
                  />
                </Stack>
                {selected.cuerpo && (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selected.cuerpo}
                  </Typography>
                )}
                {selected.fechaVencimiento && (
                  <Typography variant="body2" color="text.secondary">
                    Vencimiento:{' '}
                    {new Date(selected.fechaVencimiento).toLocaleString('es-AR')}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Registrada: {new Date(selected.fechaCreacion).toLocaleString('es-AR')} ·{' '}
                  {selected.usuario}
                </Typography>
                {selected.contactoId && (
                  <Typography variant="body2">
                    Contacto:{' '}
                    <Link
                      component={RouterLink}
                      to={ROUTES.crm.contactos.editar(selected.contactoId)}
                      underline="hover"
                    >
                      {selected.contactoNombre ?? `#${selected.contactoId}`}
                    </Link>
                  </Typography>
                )}
                {selected.oportunidadId && (
                  <Typography variant="body2">
                    Oportunidad:{' '}
                    <Link
                      component={RouterLink}
                      to={ROUTES.crm.oportunidades.detalle(selected.oportunidadId)}
                      underline="hover"
                    >
                      {selected.oportunidadNombre ?? `#${selected.oportunidadId}`}
                    </Link>
                  </Typography>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setSelected(null)} sx={{ textTransform: 'none' }}>
                Cerrar
              </Button>
              <Button
                variant="contained"
                color={selected.completada ? 'warning' : 'success'}
                startIcon={selected.completada ? <PendienteIcon /> : <CompletarIcon />}
                disabled={savingEstado}
                onClick={() => void handleToggleEstado(selected)}
                sx={{ textTransform: 'none' }}
              >
                {selected.completada ? 'Marcar pendiente' : 'Marcar completada'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CrmAgenda;
